/**
 * The license search's primary filters — Call Sign, Name, State, Service Type,
 * License Status and the expiration range.
 *
 * Every control is built once, here, and the page arranges them. That rule survives
 * from the three-layout experiment this file used to hold: the layouts were only ever
 * allowed to arrange fields, never to re-implement one, which is why deleting two of
 * them changed no behaviour.
 *
 * What that experiment settled, and is kept: the Name input is full width with the
 * licensee/contact scope selector on its own line underneath (sharing the row made it
 * visibly narrower than Call Sign beside it, which is what prompted the work), and the
 * expiration range no longer spans two grid columns.
 */
import { ReactNode } from 'react'

import type { FacetValue, LicenseFacets, LicenseSearchParams } from '../../types'
import {
  FREQUENCY_TOLERANCES, frequencyWindow, MAX_FREQUENCY_MHZ, parseFrequency,
} from '../../utils/frequency'

// ---------------------------------------------------------------------------
// Preset date range (moved here from SearchPage so the layouts can arrange it)
// ---------------------------------------------------------------------------

export interface ExpiryRange {
  start?: string
  end?: string
}

export interface ExpiryPreset {
  value: string
  label: string
  /** Evaluated lazily — a preset is relative to the day it is chosen, not to page load. */
  range: () => ExpiryRange
}

export interface PresetDateRangeProps {
  idPrefix: string
  label: string
  presets: ExpiryPreset[]
  preset: string
  onPresetChange: (value: string) => void
  start?: string
  end?: string
  onStartChange: (value: string) => void
  onEndChange: (value: string) => void
  onUnlock: () => void
  /** Shown in place of the help text when the range cannot match anything. */
  warning?: string
  /** True when a named (relative) range is active, so the dates are shown locked. */
  derived: boolean
}

/**
 * A named date range plus the two dates it currently resolves to.
 *
 * When a preset is active the dates are shown but disabled, with a note saying they
 * are recalculated on every run and an "Edit dates" button that converts the range to
 * a custom one you own. See the block comment above EXPIRY_RANGE_PRESETS in
 * SearchPage.tsx for why the dates are visible-but-locked rather than hidden or
 * freely editable.
 */
export function PresetDateRange({
  idPrefix, label, presets, preset, onPresetChange,
  start, end, onStartChange, onEndChange, onUnlock, warning, derived,
}: PresetDateRangeProps) {
  const noteId = `${idPrefix}_note`

  const dateInput = (which: 'start' | 'end', value?: string, onChange?: (v: string) => void) => (
    <div>
      <label htmlFor={`${idPrefix}_${which}`} className="block text-xs text-gray-500 mb-0.5">
        {which === 'start' ? 'From' : 'To'}
      </label>
      <input
        id={`${idPrefix}_${which}`}
        type="date"
        className="input"
        value={value || ''}
        onChange={(e) => onChange?.(e.target.value)}
      />
    </div>
  )

  const note = warning ? (
    <p className="mt-1 text-xs text-amber-700">{warning}</p>
  ) : derived ? (
    // A named range shows the window it currently resolves to as TEXT, not as two
    // disabled date inputs. The dates were visible-but-disabled until 2026-08-15,
    // which read as a broken control — an input you cannot type in invites a click
    // and explains nothing. The transparency that design was protecting is the
    // *window*, and a sentence carries it better than a greyed-out picker.
    <p id={noteId} className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-gray-500">
      <span>
        {start && end ? `${start} to ${end}` : end ? `On or before ${end}` : `From ${start}`}
        , recalculated each time this search runs.
      </span>
      <button
        type="button"
        onClick={onUnlock}
        className="py-0.5 text-primary-600 hover:text-primary-800 underline"
      >
        Edit dates
      </button>
    </p>
  ) : (
    <p className="mt-1 text-xs text-gray-500">
      Leave either date blank for an open-ended range.
    </p>
  )

  return (
    <div>
      <label htmlFor={`${idPrefix}_preset`} className="label">{label}</label>
      <select
        id={`${idPrefix}_preset`}
        className="input"
        value={preset}
        onChange={(e) => onPresetChange(e.target.value)}
      >
        {presets.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      {/* The pickers appear only when the dates are yours to set. The dropdown
          already says what a named range searches, and the note below says which
          window it works out to today. */}
      {!derived && (
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {dateInput('start', start, onStartChange)}
          {dateInput('end', end, onEndChange)}
        </div>
      )}

      {note}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Individual fields
// ---------------------------------------------------------------------------

/**
 * `<option>`s for a filter select, from the facets endpoint.
 *
 * `selected` is the value currently in the search params. If the facets haven't
 * loaded, or the data no longer contains that value (a saved search from before a
 * state was dropped, say), it is still rendered — otherwise the select would silently
 * snap to "All …" and the user would run a different search than the one shown.
 */
function facetOptions(
  values: FacetValue[] | undefined,
  selected: string | undefined,
  format: (f: FacetValue) => string = (f) => f.label,
) {
  const list = values ?? []
  const options = list.map((f) => (
    <option key={f.code} value={f.code}>{format(f)}</option>
  ))
  if (selected && !list.some((f) => f.code === selected)) {
    options.unshift(<option key={selected} value={selected}>{selected}</option>)
  }
  return options
}

interface FieldProps {
  params: LicenseSearchParams
  onChange: (patch: Partial<LicenseSearchParams>) => void
  facets: LicenseFacets | null
}

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return <label htmlFor={htmlFor} className="label">{children}</label>
}

/**
 * The one search box.
 *
 * Replaces the separate Call Sign and Name inputs: the server routes a term to only
 * the fields it could possibly match, so one box asks the question people actually
 * arrive with ("who is WPX123?", "who is 308-632-7836?") without making them decide
 * which field it belongs in first.
 *
 * It matches licensees **and** filing contacts, with no selector. The selector that
 * used to sit here asked the user which party the name belonged to — which is the
 * thing they are searching to find out. Asking for one specifically is what the
 * Licensee name / Contact name fields in Advanced Filters are for, and those can be
 * combined, which a selector never could.
 *
 * The help text describes what the user will see, not how it is done — no "prefix",
 * no "full text". It has to be there: results changing as you type is only obvious
 * once it happens, and the reason a half-typed word matches but a fragment inside a
 * word does not is otherwise unguessable.
 */
export function SearchTermField({ value, onSearchChange, minLength }: {
  value: string
  onSearchChange: (value: string) => void
  minLength: number
}) {
  const tooShort = value.trim().length > 0 && value.trim().length < minLength
  return (
    <div>
      <FieldLabel htmlFor="search">Search</FieldLabel>
      <input
        id="search"
        type="search"
        className="input"
        placeholder="Call sign, name, city, phone, email or FRN"
        value={value}
        onChange={(e) => onSearchChange(e.target.value)}
      />
      <p className="mt-1 text-xs text-gray-500">
        {tooShort
          ? 'Keep typing — three characters or more.'
          : 'Searches call signs, licensees and their filing contacts. Results update as you type, matching from the start of a word; press Search to match inside words too.'}
      </p>
    </div>
  )
}



/** A plain text filter for Advanced Filters — one definition, six uses. */
export function TextFilterField({
  id, label, hint, placeholder, maxLength, value, onChange, transform,
}: {
  id: keyof LicenseSearchParams
  label: string
  hint?: string
  placeholder: string
  maxLength: number
  value: string
  onChange: (patch: Partial<LicenseSearchParams>) => void
  transform?: (raw: string) => string
}) {
  return (
    <div>
      <FieldLabel htmlFor={id}>
        {label}
        {hint && <span className="ml-1 text-xs font-normal text-gray-400">{hint}</span>}
      </FieldLabel>
      <input
        id={id}
        type="text"
        className="input"
        placeholder={placeholder}
        maxLength={maxLength}
        value={value}
        onChange={(e) => onChange({ [id]: transform ? transform(e.target.value) : e.target.value })}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Frequency
// ---------------------------------------------------------------------------

/**
 * Frequency, and how close a match counts.
 *
 * The two belong in one control because neither answers the question alone: the
 * number is what the user has, and the window is what makes it findable. FCC stores
 * five decimals of MHz and a radio shows three, so a typed 462.563 has to reach a
 * stored 462.56250 — which is what the sentence underneath is for. Without it, a
 * search that quietly covered two channels, or none, would look identical to one
 * that covered the right one.
 */
export function FrequencyField({
  value, onValueChange, tolerance, onToleranceChange,
  includeBandGrants, onIncludeBandGrantsChange,
}: {
  value: string
  onValueChange: (raw: string) => void
  tolerance: number
  onToleranceChange: (khz: number) => void
  includeBandGrants: boolean
  onIncludeBandGrantsChange: (on: boolean) => void
}) {
  const typed = value.trim()
  const parsed = parseFrequency(typed)
  const options = FREQUENCY_TOLERANCES.some((o) => o.value === tolerance)
    ? FREQUENCY_TOLERANCES
    // A saved search may carry a width this build's list does not offer. Showing it
    // keeps the select from snapping to a different window than the one being run.
    : [...FREQUENCY_TOLERANCES, { value: tolerance, label: `± ${tolerance} kHz` }]

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <FieldLabel htmlFor="frequency_mhz">
          Frequency
          <span className="ml-1 text-xs font-normal text-gray-400">(MHz)</span>
        </FieldLabel>
        <input
          id="frequency_mhz"
          type="text"
          inputMode="decimal"
          className="input"
          placeholder="e.g. 154.5700"
          maxLength={12}
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          aria-describedby="frequency_note"
        />
      </div>
      {/* Only once there is a frequency to be close TO. On its own it is a
          control for a question nobody has asked yet, and it doubled the visual
          weight of a field most searches never use. */}
      {parsed !== null && (
        <div>
          <FieldLabel htmlFor="frequency_tolerance_khz">How close a match</FieldLabel>
          <select
            id="frequency_tolerance_khz"
            className="input"
            value={tolerance}
            onChange={(e) => onToleranceChange(Number(e.target.value))}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      )}
      {/* Only once a frequency has been typed, like the tolerance: it is a
          refinement of a question nobody has asked yet.

          Off by default because on real data it inverts the result. A search for
          152.480 returns 90 licenses holding an assignment on that channel and
          194 more whose only claim is a blanket 150–174 MHz authorisation — so
          the answer to "who is on this channel" arrives outnumbered two to one by
          licensees who merely could be. */}
      {parsed !== null && (
        <label className="sm:col-span-2 -mt-1 flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={includeBandGrants}
            onChange={(e) => onIncludeBandGrantsChange(e.target.checked)}
          />
          <span>
            Also include wide-band authorizations
            <span className="block text-xs text-gray-500">
              Licensees authorized across a whole band that covers this frequency —
              they <em>may</em> transmit here, but are not assigned to it. Shown as a
              range in the Match column.
            </span>
          </span>
        </label>
      )}

      <p id="frequency_note" className="sm:col-span-2 -mt-2 text-xs text-gray-500">
        {typed && parsed === null ? (
          <span className="text-amber-700">
            Enter a frequency in MHz, between 0 and {MAX_FREQUENCY_MHZ.toLocaleString()}
            {' '}— for example 154.5700. Nothing is being filtered by frequency yet.
          </span>
        ) : parsed !== null ? (
          <>
            Finds licenses authorized anywhere from{' '}
            <span className="font-medium">{frequencyWindow(parsed, tolerance)[0]}</span> to{' '}
            <span className="font-medium">{frequencyWindow(parsed, tolerance)[1]}</span> MHz,
            including any licensed for a whole band that covers it.
          </>
        ) : (
          'Finds every license authorized on a frequency, including those licensed for a whole band that covers it.'
        )}
      </p>
    </div>
  )
}

export function StateField({ params, onChange, facets }: FieldProps) {
  return (
    <div>
      <FieldLabel htmlFor="state">State</FieldLabel>
      <select
        id="state"
        className="input"
        value={params.state || ''}
        onChange={(e) => onChange({ state: e.target.value })}
      >
        <option value="">All States</option>
        {facetOptions(facets?.states, params.state)}
      </select>
    </div>
  )
}

export function ServiceField({ params, onChange, facets }: FieldProps) {
  return (
    <div>
      <FieldLabel htmlFor="service_code">Service Type</FieldLabel>
      <select
        id="service_code"
        className="input"
        value={params.service_code || ''}
        onChange={(e) => onChange({ service_code: e.target.value })}
      >
        <option value="">All Services</option>
        {facetOptions(facets?.services, params.service_code, (f) => `${f.code} - ${f.label}`)}
      </select>
    </div>
  )
}

export function StatusField({ params, onChange, facets }: FieldProps) {
  return (
    <div>
      <FieldLabel htmlFor="license_status">License Status</FieldLabel>
      <select
        id="license_status"
        className="input"
        value={params.status || ''}
        onChange={(e) => onChange({ status: e.target.value })}
      >
        <option value="">All Statuses</option>
        {facetOptions(facets?.statuses, params.status)}
      </select>
    </div>
  )
}

/**
 * Do Not Contact handling.
 *
 * Action/Absolute reported somebody on Do Not Call still appearing in results and
 * nearly being rung again. Three options rather than a tick-box, because all three
 * are real questions: everything, the working list, and "show me what is suppressed"
 * for auditing the list without leaving the page.
 *
 * Matches call sign, entity name AND usi — the same license-shaped types that suppress
 * a send — so Exclude cannot leave in somebody the send path would refuse.
 */
export function DncField({ params, onChange }: FieldProps) {
  return (
    <div>
      <FieldLabel htmlFor="dnc_status">Do Not Contact</FieldLabel>
      <select
        id="dnc_status"
        className="input"
        value={params.dnc_status || 'any'}
        onChange={(e) =>
          onChange({ dnc_status: e.target.value as LicenseSearchParams['dnc_status'] })
        }
      >
        <option value="any">Include suppressed</option>
        <option value="exclude">Hide suppressed</option>
        <option value="only">Only suppressed</option>
      </select>
    </div>
  )
}

/** Mirrors `construction_filter.CHOICES` on the server. */
const CONSTRUCTION_CHOICES = [30, 60, 90, 120, 180, 365]

/**
 * Construction notification due (P8): the prospecting half of Construction
 * Deadlines, here so that Add as Lead, bulk add and saved searches that feed leads
 * all work for it, as they do for expiring licenses. Choosing a window also clears a
 * relative expiration range (SearchPage), because a license with a construction
 * deadline is usually about a year old and nine years from expiring.
 */
export function ConstructionField({ params, onChange }: FieldProps) {
  const value = params.construction_within_days
  return (
    <div>
      <FieldLabel htmlFor="construction_within_days">Construction notification due</FieldLabel>
      <select
        id="construction_within_days"
        className="input"
        value={value ? String(value) : ''}
        onChange={(e) =>
          onChange({ construction_within_days: e.target.value ? Number(e.target.value) : undefined })
        }
      >
        <option value="">Any (not filtered)</option>
        {CONSTRUCTION_CHOICES.map((d) => (
          <option key={d} value={d}>Within {d} days</option>
        ))}
      </select>
      {value ? (
        <p className="mt-1 text-xs text-gray-500">
          Also includes deadlines missed in the last year. Licenses like these are usually years
          from expiring, so choosing this sets the expiration range to any date, and choosing
          an expiration range sets this back to any.
        </p>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// The filter grid
// ---------------------------------------------------------------------------

export interface PrimaryFiltersProps {
  /** The one search box's raw value, and its setter. Held by the page because it
      drives its own debounced request rather than the form submit. */
  searchTerm: string
  onSearchTermChange: (value: string) => void
  minSearchLength: number
  params: LicenseSearchParams
  onChange: (patch: Partial<LicenseSearchParams>) => void
  facets: LicenseFacets | null
  /** Everything the expiration range needs; the presets themselves stay in SearchPage
      because `test_date_presets.py` reads that file to check the keys match Python. */
  expiration: Omit<PresetDateRangeProps, 'idPrefix' | 'label'>
}

/** The search box on its own row, then the filters that narrow it. */
export function PrimaryFilters({
  searchTerm, onSearchTermChange, minSearchLength,
  params, onChange, facets, expiration,
}: PrimaryFiltersProps) {
  const fieldProps = { params, onChange, facets }
  return (
    <div className="space-y-4">
      <SearchTermField
        value={searchTerm}
        onSearchChange={onSearchTermChange}
        minLength={minSearchLength}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 border-t border-gray-200 pt-4">
        <StateField {...fieldProps} />
        <ServiceField {...fieldProps} />
        <StatusField {...fieldProps} />
        <ConstructionField {...fieldProps} />
        <PresetDateRange {...expiration} idPrefix="expiration" label="Expiration date" />
      </div>
    </div>
  )
}
